import { formatCost, formatLatency, RequestStrip, UsageTotal } from "@dashboard/atticShared/usage";
import { DashboardCard } from "@dashboard/components/Card";
import { Date as DayLabel } from "@dashboard/components/Date/Date";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { usageMessages } from "../../messages";
import { type AtticApiKeyUsageRow } from "../../utils";

export interface AtticApiKeyUsageCardProps {
  /** One row per UTC day of the window, oldest first, quiet days at zero. */
  rows: AtticApiKeyUsageRow[];
  totals: Omit<AtticApiKeyUsageRow, "day">;
}

export const AtticApiKeyUsageCard = ({ rows, totals }: AtticApiKeyUsageCardProps) => {
  const intl = useIntl();
  // Newest first, and only days that saw a request: thirty rows of zeros would
  // bury the few that matter.
  const activeDays = rows.filter(row => row.requests > 0).reverse();

  return (
    <DashboardCard data-test-id="attic-api-key-usage">
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(usageMessages.title)}</DashboardCard.Title>
        <DashboardCard.Subtitle>
          {intl.formatMessage(usageMessages.description)}
        </DashboardCard.Subtitle>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Box display="grid" gridTemplateColumns={{ mobile: 2, desktop: 5 }} gap={5}>
          <UsageTotal
            label={intl.formatMessage(usageMessages.requests)}
            value={intl.formatNumber(totals.requests)}
            testId="attic-api-key-usage-requests"
          />
          <UsageTotal
            label={intl.formatMessage(usageMessages.errors)}
            value={intl.formatNumber(totals.errors)}
            testId="attic-api-key-usage-errors"
          />
          <UsageTotal
            label={intl.formatMessage(usageMessages.throttled)}
            value={intl.formatNumber(totals.throttled)}
            testId="attic-api-key-usage-throttled"
          />
          <UsageTotal
            label={intl.formatMessage(usageMessages.cost)}
            value={formatCost(intl, totals.cost)}
            testId="attic-api-key-usage-cost"
          />
          <UsageTotal
            label={intl.formatMessage(usageMessages.latency)}
            value={formatLatency(intl, totals.meanLatencyMs)}
            testId="attic-api-key-usage-latency"
          />
        </Box>
        {activeDays.length === 0 ? (
          <Text
            as="p"
            size={3}
            color="default2"
            marginTop={5}
            data-test-id="attic-api-key-usage-none"
          >
            {intl.formatMessage(usageMessages.none)}
          </Text>
        ) : (
          <Box marginTop={5}>
            <RequestStrip rows={rows} testId="attic-api-key-usage-strip" />
          </Box>
        )}
      </DashboardCard.Content>
      {activeDays.length > 0 && (
        <ResponsiveTable>
          <TableHead>
            <TableRowLink>
              <TableCell>{intl.formatMessage(usageMessages.day)}</TableCell>
              <TableCell align="right">{intl.formatMessage(usageMessages.requests)}</TableCell>
              <TableCell align="right">{intl.formatMessage(usageMessages.errors)}</TableCell>
              <TableCell align="right">{intl.formatMessage(usageMessages.throttled)}</TableCell>
              <TableCell align="right">{intl.formatMessage(usageMessages.cost)}</TableCell>
              <TableCell align="right">{intl.formatMessage(usageMessages.latency)}</TableCell>
            </TableRowLink>
          </TableHead>
          <TableBody>
            {activeDays.map(row => (
              <TableRowLink key={row.day} data-test-id="attic-api-key-usage-row">
                <TableCell>
                  <DayLabel date={row.day} plain />
                </TableCell>
                <TableCell align="right">{intl.formatNumber(row.requests)}</TableCell>
                <TableCell align="right">{intl.formatNumber(row.errors)}</TableCell>
                <TableCell align="right">{intl.formatNumber(row.throttled)}</TableCell>
                <TableCell align="right">{formatCost(intl, row.cost)}</TableCell>
                <TableCell align="right">{formatLatency(intl, row.meanLatencyMs)}</TableCell>
              </TableRowLink>
            ))}
          </TableBody>
        </ResponsiveTable>
      )}
    </DashboardCard>
  );
};

export default AtticApiKeyUsageCard;
